import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "space-y-4 p-4 sm:p-6 lg:p-8" }
const _hoisted_2 = { class: "text-xl font-semibold text-gray-800" }
const _hoisted_3 = {
  key: 0,
  class: "mt-4 flex bg-gray-50 px-4 py-4 shadow ring-1 ring-gray-300 sm:p-6 md:rounded"
}
const _hoisted_4 = { class: "text-center text-xs font-medium text-gray-500" }
const _hoisted_5 = { class: "mt-1 text-center text-2xl font-semibold text-gray-800" }
const _hoisted_6 = {
  key: 2,
  class: "mt-4 grid grid-cols-1 gap-4 md:grid-cols-2"
}

import { ref, Ref, computed, watch, ComputedRef, onMounted } from 'vue';
import moment from 'moment';

import store from '@/store';
import { useFilterStore, useUiStore } from '@/stores';
import { toastServiceError } from '@/services/core/notification';
import CallReportService from '@/services/api/CallReportService';
import { formatSecToTime } from '@/utils/datetime';

import {
    CallReportActions,
    CallReportGetters,
    CallStatus,
    CallType,
    CallReportFilter,
    CallReportServiceGetInfoParams,
    CallsStats,
    DataItem,
    FilterItem,
    LevelModel,
} from '@/types';

import { CallReportFilters } from '@/views/pages/reports/call/components';


export default /*@__PURE__*/_defineComponent({
  __name: 'CallReportPage',
  setup(__props) {

const callsStats: Ref<CallsStats | null> = ref(null);

const levelFilter = computed<LevelModel | null>(() => filterStore.level);
const dateRange = computed<Array<Date>>({
    get: () => filterStore.dateRange,
    set: (dateRange: Array<Date>) => filterStore.setDateRange(dateRange),
});
const filter = computed<CallReportFilter>(() => store.getters[CallReportGetters.FILTER]);
const activeFilters: ComputedRef<FilterItem[]> = computed(
    () => store.getters[CallReportGetters.ACTIVE_FILTERS],
);

const filterStore = useFilterStore();
const uiStore = useUiStore();

watch([levelFilter, dateRange, activeFilters], () => {
    fetchReportData();
});

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const fetchReportData = async () => {
    const params: CallReportServiceGetInfoParams = {
        startDate: moment.utc(dateRange.value[0]).format('YYYY-MM-DD'),
        endDate: moment.utc(dateRange.value[1]).format('YYYY-MM-DD'),
        type: filter.value.type?.value || null,
        status: filter.value.status?.value || null,
        user: filter.value.user?.id || null,
        userLabel: filter.value.userLabel?.id || null,
        number: filter.value.number?.number || null,
        serviceTimeRange: filter.value.serviceTimeRange?.value || null,
    };

    try {
        setLoading(true);
        const response = await CallReportService.getInfo<CallsStats>({ params });
        callsStats.value = response.data;
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const handleRemoveFilter = (filter: FilterItem) => {
    store.dispatch(CallReportActions.CLEAR_FILTER, filter.field);
};

const headerStats = computed<DataItem<string>[]>(() => {
    if (!callsStats.value) return [];

    return [
        { name: 'totalCalls', value: callsStats.value.total.toString() },
        { name: 'inboundCalls', value: callsStats.value.type[CallType.INBOUND].toString() },
        { name: 'outboundCalls', value: callsStats.value.type[CallType.OUTBOUND].toString() },
        { name: 'noAnswer', value: callsStats.value.status[CallStatus.NO_ANSWER].toString() },
        { name: 'busy', value: callsStats.value.status[CallStatus.BUSY].toString() },
        {
            name: 'averageServiceTime',
            value: moment.utc((callsStats.value.averageServiceTime || 0) * 1000).format('HH:mm:ss'),
        },
        {
            name: 'averageWaitingTime',
            value: callsStats.value.averageWaitingTime
                ? formatSecToTime(callsStats.value.averageWaitingTime, true)
                : '-',
        },
    ];
});

onMounted(() => {
    fetchReportData();
});

return (_ctx: any,_cache: any) => {
  const _component_UiDatePicker = _resolveComponent("UiDatePicker")!
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!
  const _component_UiBadge = _resolveComponent("UiBadge")!
  const _component_UiPanel = _resolveComponent("UiPanel")!
  const _component_CallStatusDoughnutChart = _resolveComponent("CallStatusDoughnutChart")!
  const _component_CallStatusPerLabelBarChart = _resolveComponent("CallStatusPerLabelBarChart")!
  const _component_CallStatusPerDayBarChart = _resolveComponent("CallStatusPerDayBarChart")!
  const _component_CallTypesPerDayBarChart = _resolveComponent("CallTypesPerDayBarChart")!
  const _component_CallAverageTimePerDayBarChart = _resolveComponent("CallAverageTimePerDayBarChart")!
  const _component_CallTimeOfDayHeatmapChart = _resolveComponent("CallTimeOfDayHeatmapChart")!
  const _component_CallServiceTimeRangesDoughnutChart = _resolveComponent("CallServiceTimeRangesDoughnutChart")!
  const _component_CallMediasDoughnutChart = _resolveComponent("CallMediasDoughnutChart")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_UiPageHeader, null, {
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_2, _toDisplayString(_ctx.$t('report.call.title')), 1)
      ]),
      actions: _withCtx(() => [
        _createVNode(_unref(CallReportFilters)),
        _createVNode(_component_UiDatePicker, {
          modelValue: dateRange.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((dateRange).value = $event)),
          range: "",
          multiCalendars: "",
          class: "ml-4"
        }, null, 8, ["modelValue"])
      ]),
      _: 1
    }),
    (activeFilters.value.length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activeFilters.value, (filter) => {
            return (_openBlock(), _createBlock(_component_UiBadge, {
              key: filter,
              removable: "",
              onRemove: ($event: any) => (handleRemoveFilter(filter))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t(`report.call.filters.activeTag.${filter.field}`)) + ": " + _toDisplayString(filter.label), 1)
              ]),
              _: 2
            }, 1032, ["onRemove"]))
          }), 128))
        ]))
      : _createCommentVNode("", true),
    (headerStats.value)
      ? (_openBlock(), _createElementBlock("dl", {
          key: 1,
          class: _normalizeClass(["mt-4 grid grid-cols-2 gap-4", ['sm:grid-cols-' + headerStats.value.length]])
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(headerStats.value, (stat) => {
            return (_openBlock(), _createBlock(_component_UiPanel, {
              key: stat.name
            }, {
              default: _withCtx(() => [
                _createElementVNode("dt", _hoisted_4, _toDisplayString(_ctx.$t('report.call.stats.' + stat.name)), 1),
                _createElementVNode("dd", _hoisted_5, _toDisplayString(stat.value), 1)
              ]),
              _: 2
            }, 1024))
          }), 128))
        ], 2))
      : _createCommentVNode("", true),
    (callsStats.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
          _createVNode(_component_CallStatusDoughnutChart, {
            title: _ctx.$t('report.call.callStatusDoughnutChart.title'),
            data: callsStats.value.status,
            class: "col-span-2 h-[300px] md:col-span-1"
          }, null, 8, ["title", "data"]),
          _createVNode(_component_CallStatusPerLabelBarChart, {
            title: _ctx.$t('report.call.callStatusPerLabelBarChart.title'),
            data: callsStats.value.statusPerLabel,
            class: "col-span-2 h-[300px] md:col-span-1"
          }, null, 8, ["title", "data"]),
          _createVNode(_component_CallStatusPerDayBarChart, {
            title: _ctx.$t('report.call.callStatusPerDayBarChart.title'),
            data: callsStats.value.statusPerDay,
            class: "col-span-2 hidden h-[300px] sm:block"
          }, null, 8, ["title", "data"]),
          _createVNode(_component_CallTypesPerDayBarChart, {
            title: _ctx.$t('report.call.callTypePerDayBarChart.title'),
            data: callsStats.value.typePerDay,
            class: "col-span-2 h-[300px] md:col-span-1"
          }, null, 8, ["title", "data"]),
          _createVNode(_component_CallAverageTimePerDayBarChart, {
            title: _ctx.$t('report.call.callAverageTimesChart.title'),
            data: callsStats.value.tmPerDay,
            class: "col-span-2 h-[300px] md:col-span-1"
          }, null, 8, ["title", "data"]),
          _createVNode(_component_CallTimeOfDayHeatmapChart, {
            title: _ctx.$t('report.call.callTimeOfDayHeatmapChart.title'),
            data: callsStats.value.timeOfDay,
            class: "col-span-2 hidden h-[300px] sm:block"
          }, null, 8, ["title", "data"]),
          _createVNode(_component_CallServiceTimeRangesDoughnutChart, {
            title: _ctx.$t('report.call.callServiceTimeRangesDoughnutChart.title'),
            data: callsStats.value.serviceTimeRange,
            class: "col-span-2 h-[300px] md:col-span-1"
          }, null, 8, ["title", "data"]),
          _createVNode(_component_CallMediasDoughnutChart, {
            title: _ctx.$t('report.call.callMediasDoughnutChart.title'),
            data: callsStats.value.media,
            class: "col-span-2 h-[300px] md:col-span-1"
          }, null, 8, ["title", "data"])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})